import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { CompanyContextService } from '../../services/company-context.service';
import { wantsCompanyHeader } from './module-api';

/**
 * Stamps the active company onto backend calls as `X-Company-Id`.
 *
 * Company-scoped backends read the company from the ROUTE
 * (`/api/v1/tenants/{id}/…`); the header is a cross-check, not the source:
 * when both are present and disagree, the backend answers
 * `400 TENANT_MISMATCH`. That turns the bug "module kept a URL across a
 * company switch" into an immediate, attributable error instead of a silent
 * answer from the wrong company.
 *
 * Only the module backends that declare `companyHeader` get it
 * (`environment.api.modules`) — not UMC, not Identity, not third-party URLs a
 * module may call, and not a backend whose CORS policy does not list the
 * header. Requests made before a company is active carry no header; endpoints
 * that need one answer `400 TENANT_MISSING`, which is the honest state.
 *
 * Must run *after* `apiUrlInterceptor`, so the URL is resolved and the
 * base check can tell the backend apart from other endpoints.
 */
export const companyIdInterceptor: HttpInterceptorFn = (request, next) => {
  const companyId = inject(CompanyContextService).activeCompanyId();

  if (companyId === null || !wantsCompanyHeader(request.url)) {
    return next(request);
  }

  return next(request.clone({ setHeaders: { 'X-Company-Id': String(companyId) } }));
};
